import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { WitnessStatsCard } from "@/components/WitnessStatsCard";
import { api } from "@/lib/api";
import { Landmark, CalendarDays, Users, MapPin, ChevronRight, ExternalLink } from "lucide-react";

export default function ElectionCentre() {
  const [munis, setMunis] = useState([]);
  const [elections, setElections] = useState([]);
  const [offices, setOffices] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/public/municipalities").then((r) => setMunis(r.data)).catch(() => {});
    api.get("/public/elections").then((r) => setElections(r.data)).catch(() => {});
    api.get("/public/offices").then((r) => setOffices(r.data)).catch(() => {});
    api.get("/public/candidates").then((r) => setCandidates(r.data)).catch(() => {});
  }, []);

  const filtered = query ? munis.filter((m) => m.name.toLowerCase().includes(query.toLowerCase())) : munis;

  return (
    <div className="wyv-container py-12 sm:py-16">
      <div className="max-w-2xl">
        <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 mb-4">Ontario Election Centre</Badge>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="election-centre-title">
          Know your election.
        </h1>
        <p className="text-muted-foreground mt-3 leading-relaxed">
          Find your municipality and ward, see who is running, and check important voting dates. Witness Your Vote
          links to public information — always confirm details with your municipal Clerk's office, the official
          source for election information.
        </p>
        <div className="flex flex-wrap gap-3 mt-6">
          <Button asChild data-testid="ec-find-my-ward"><Link to="/elections/find-my-ward">Find My Ward</Link></Button>
          <Button asChild variant="outline"><Link to="/candidates">Who's Running</Link></Button>
        </div>
      </div>

      <div className="mt-10 max-w-xl">
        <label htmlFor="ec-search" className="text-sm font-semibold text-foreground block mb-2">Search your municipality</label>
        <Input id="ec-search" placeholder="e.g. Kawartha Lakes" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="ec-muni-search" />
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-5" data-testid="ec-municipality-list">
        {filtered.map((m) => {
          const els = elections.filter((e) => e.municipality_id === m.id);
          return els.map((e) => {
            const eOffices = offices.filter((o) => o.election_id === e.id);
            const eCands = candidates.filter((c) => c.election.id === e.id);
            const wards = eOffices.filter((o) => o.ward_label);
            return (
              <Card key={e.id}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <h2 className="font-heading font-semibold text-lg text-foreground flex items-center gap-2">
                        <Landmark className="h-5 w-5 text-accent" /> {m.name}
                      </h2>
                      <p className="text-sm text-muted-foreground mt-0.5">{e.label}</p>
                    </div>
                    <Badge variant="outline" className={e.status === "open" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-700 border-slate-300"}>
                      {e.status === "open" ? "Declarations open" : e.status}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-5 text-center">
                    <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                      <MapPin className="h-4 w-4 text-accent mx-auto mb-1" />
                      <p className="font-heading font-bold text-lg tabular-nums">{wards.length}</p>
                      <p className="text-[11px] text-muted-foreground">Wards</p>
                    </div>
                    <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                      <Users className="h-4 w-4 text-accent mx-auto mb-1" />
                      <p className="font-heading font-bold text-lg tabular-nums">{eCands.length}</p>
                      <p className="text-[11px] text-muted-foreground">Participating</p>
                    </div>
                    <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                      <CalendarDays className="h-4 w-4 text-accent mx-auto mb-1" />
                      <p className="font-heading font-bold text-sm">{new Date(e.voting_opens_at).toLocaleDateString("en-CA", { month: "short", day: "numeric" })}</p>
                      <p className="text-[11px] text-muted-foreground">Voting opens</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-4 inline-flex items-center gap-1.5">
                    <CalendarDays className="h-3.5 w-3.5" />
                    {new Date(e.voting_opens_at).toLocaleString("en-CA", { month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })} –{" "}
                    {new Date(e.voting_closes_at).toLocaleString("en-CA", { month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
                  </p>
                  <div className="flex flex-wrap gap-2.5 mt-5">
                    <Button asChild size="sm"><Link to="/elections/find-my-ward">Find my ward</Link></Button>
                    <Button asChild size="sm" variant="outline"><Link to="/candidates">See candidates</Link></Button>
                    {m.slug === "kawartha-lakes" && (
                      <Button asChild size="sm" variant="outline">
                        <Link to="/kawartha-lakes-2026">2026 facts page <ChevronRight className="h-3.5 w-3.5 ml-1" /></Link>
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          });
        })}
      </div>

      <div className="mt-8 max-w-3xl">
        <WitnessStatsCard title="WitnessYourVote participation across active elections" />
      </div>

      <Card className="mt-8 max-w-3xl border-t-4 border-t-primary" data-testid="election-night-readiness-card">
        <CardContent className="p-6">
          <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 text-[10px] uppercase mb-2">
            Election Night — Not Yet Active
          </Badge>
          <h2 className="font-heading font-semibold text-sm mb-2">After the official count</h2>
          <p className="text-xs text-muted-foreground leading-relaxed" data-testid="election-night-separation">
            Official results are published by the municipal clerk and are the only legal outcome of the
            election. WitnessYourVote declarations are voluntary records from a self-selected group of
            participants — they are <span className="font-semibold text-foreground">not a ballot count, not official results</span>,
            and differences between the two are expected and normal. A difference is not evidence of fraud.
            When election-night mode is activated after the count, this page will show official results
            (attributed to their official source) alongside — but strictly separate from — WitnessYourVote
            participation context.
          </p>
        </CardContent>
      </Card>

      <Card className="mt-8 bg-secondary max-w-3xl">
        <CardContent className="p-6">
          <h2 className="font-heading font-semibold text-sm mb-2">Don't see your municipality?</h2>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Witness Your Vote is expanding across Ontario. Invite a candidate from your community or contact us to
            request your municipality — and consult your municipal Clerk's website for official election information{" "}
            <ExternalLink className="h-3 w-3 inline" />.
          </p>
          <div className="flex gap-2.5 mt-4">
            <Button asChild size="sm" variant="outline"><Link to="/invite-a-candidate">Invite a candidate</Link></Button>
            <Button asChild size="sm" variant="outline"><Link to="/contact">Contact us</Link></Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
